import { useQuery } from '@tanstack/react-query';
import {
  Check,
  ChevronDown,
  Download,
  FileText,
  Maximize2,
  Printer,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useMe } from '@/auth/useAuth';
import { getKidSub } from '@/auth/authStore';
import { CheatsheetHtmlReview } from './CheatsheetHtmlReview';
import { Link, useSearchParams } from 'react-router-dom';
import { isLocalStudyExperience } from '@/pages/try/studyExperience/mode';
import { sampleAnalysisPath } from '@/pages/try/studyExperience/sampleProfile';
import { StudyShell } from '../StudyShell';
import { subjectLabel } from '../studyTools';
import { getStudyHome, STUDY_HOME_QUERY_KEY } from '../studyApi';
import { CheatsheetPdfPreview } from './CheatsheetPdfPreview';
import {
  listOfficialCheatsheets,
  listReviewCheatsheets,
  reviewEnabled,
  saveCheatsheetPdf,
  verifiedCheatsheetPdf,
  type OfficialCheatsheet,
} from './cheatsheetApi';

const formulaTints = [
  'bg-wash-sky border-brand-sky',
  'bg-wash-coral border-brand-coral',
  'bg-wash-mint border-brand-mint',
  'bg-wash-sunshine border-brand-sunshine',
  'bg-wash-bubblegum border-brand-bubblegum',
];

function ReferenceContent({ entry }: { entry: OfficialCheatsheet['entries'][number] }) {
  if (entry.blocks)
    return (
      <div className="mt-3 min-w-0 space-y-3">
        {entry.blocks.map((block, index) => (
          <p
            key={index}
            aria-label={block.kind === 'formula' ? `Formula: ${block.value}` : undefined}
            className={
              block.kind === 'formula'
                ? 'max-w-full overflow-x-auto rounded-xl border border-hairline bg-wash-sky px-3 py-3 text-base font-extrabold leading-7 text-ink sm:px-4 sm:text-xl sm:leading-8'
                : 'whitespace-pre-line text-sm leading-6 text-ink-soft'
            }
          >
            {block.kind === 'explain' && <strong>Use: </strong>}
            {block.kind === 'example' && <strong>Example: </strong>}
            {block.kind === 'tip' && <strong>Check: </strong>}
            {block.kind === 'formula'
              ? block.value.split(/;\s*|\n/).map((formula, formulaIndex) => (
                  <span
                    key={formulaIndex}
                    className="block w-max min-w-full whitespace-nowrap"
                    data-testid="complete-reference-formula"
                  >
                    {formula}
                  </span>
                ))
              : block.value}
          </p>
        ))}
      </div>
    );
  return (
    <div className="mt-3 min-w-0 space-y-3">
      <p
        aria-label={`Formula: ${entry.formula}`}
        className="max-w-full overflow-x-auto rounded-xl border border-hairline bg-wash-sky px-3 py-3 text-base font-extrabold leading-7 text-ink sm:px-4 sm:text-xl sm:leading-8"
      >
        {entry.formula.split(/;\s*|\n/).map((formula, index) => (
          <span
            key={index}
            className="block w-max min-w-full whitespace-nowrap"
            data-testid="complete-reference-formula"
          >
            {formula}
          </span>
        ))}
      </p>
      <p className="text-sm font-semibold leading-6 text-ink-soft">{entry.symbols}</p>
      <p className="text-sm leading-6 text-ink-soft">{entry.use_when}</p>
      <p className="text-sm font-semibold leading-6 text-ink">
        <span className="text-slate2">Example · </span>
        {entry.example}
      </p>
      <details className="border-t border-hairline pt-3 text-sm leading-6 text-ink-soft">
        <summary className="cursor-pointer font-semibold text-slate2">
          Conditions, working & common errors
        </summary>
        <div className="mt-3 space-y-3">
          {entry.conditions && (
            <p>
              <strong>Conditions: </strong>
              {entry.conditions}
            </p>
          )}
          {entry.steps && (
            <p>
              <strong>Steps: </strong>
              {entry.steps}
            </p>
          )}
          <p className="rounded-xl bg-surface p-3">
            <strong>Watch out: </strong>
            {entry.mistake}
          </p>
        </div>
      </details>
    </div>
  );
}

function SheetDiagram({
  diagram,
}: {
  diagram: NonNullable<OfficialCheatsheet['diagrams']>[number];
}) {
  return (
    <figure className="mt-4 min-w-0 max-w-full rounded-2xl border border-hairline bg-canvas-pure p-3 sm:p-4">
      <div className="max-w-full overflow-x-auto">
        <img
          src={`data:image/svg+xml;base64,${diagram.svg_base64}`}
          alt={diagram.alt}
          className="mx-auto h-auto w-full min-w-0 max-w-3xl"
        />
      </div>
      <figcaption className="mt-3 text-sm leading-6 text-slate2">{diagram.alt}</figcaption>
    </figure>
  );
}

function ReferenceNavigation({ sheet }: { sheet: OfficialCheatsheet }) {
  function collapseOtherMenus(menu: HTMLDetailsElement) {
    if (!menu.open) return;
    menu.parentElement?.querySelectorAll<HTMLDetailsElement>('details').forEach((other) => {
      if (other !== menu) other.open = false;
    });
  }
  return (
    <div
      className="flex flex-wrap items-center gap-2"
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || !(event.target instanceof HTMLElement)) return;
        const menu = event.target.closest('details');
        if (menu) {
          menu.open = false;
          menu.querySelector('summary')?.focus();
        }
      }}
    >
      <details className="group" onToggle={(event) => collapseOtherMenus(event.currentTarget)}>
        <summary className="btn-pill-ghost min-h-11 cursor-pointer list-none border border-hairline !px-3 !text-xs group-open:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink [&::-webkit-details-marker]:hidden">
          Before you calculate
        </summary>
        <ul className="absolute inset-x-0 top-full z-10 mt-2 grid gap-2 rounded-2xl border border-hairline bg-canvas-pure p-4 text-sm text-ink-soft shadow-card-soft sm:grid-cols-2">
          {sheet.checklist.split('. ').map((item) => (
            <li key={item} className="flex gap-2">
              <Check size={16} className="shrink-0 text-brand-mint" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
      </details>
      {sheet.sections && sheet.sections.length > 1 && (
        <details className="group" onToggle={(event) => collapseOtherMenus(event.currentTarget)}>
          <summary className="btn-pill-ghost min-h-11 cursor-pointer list-none border border-hairline !px-3 !text-xs group-open:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink [&::-webkit-details-marker]:hidden">
            Contents · {sheet.sections.length} sections
          </summary>
          <nav
            aria-label="Cheatsheet contents"
            className="absolute inset-x-0 top-full z-10 mt-2 grid gap-2 rounded-2xl border border-hairline bg-canvas-pure p-4 shadow-card-soft sm:grid-cols-2"
          >
            {sheet.sections.map((section, index) => (
              <a
                key={section.id}
                href={`#section-${section.id}`}
                onClick={(event) => {
                  const menu = event.currentTarget.closest('details');
                  if (menu) menu.open = false;
                }}
                className="rounded-lg px-3 py-2 text-sm text-ink-soft hover:bg-wash-sky"
              >
                {String(index + 1).padStart(2, '0')} · {section.title}
              </a>
            ))}
          </nav>
        </details>
      )}
    </div>
  );
}

function FormulaCards({ sheet }: { sheet: OfficialCheatsheet }) {
  const [answer, setAnswer] = useState(false);
  return (
    <div
      className="min-w-0 max-w-full space-y-5"
      style={{ fontFeatureSettings: '"calt" 0, "liga" 0' }}
    >
      <div className="grid min-w-0 gap-6">
        {sheet.sections?.map((section, sectionIndex) => (
          <section
            key={section.id}
            aria-labelledby={`section-${section.id}`}
            className="min-w-0 scroll-mt-5"
          >
            <h3
              id={`section-${section.id}`}
              className={`flex items-center justify-between gap-2 rounded-xl border-l-4 px-3 py-2 text-base font-extrabold text-ink ${formulaTints[sectionIndex % formulaTints.length]}`}
            >
              <span>
                {String(sectionIndex + 1).padStart(2, '0')} · {section.title}
              </span>
              {section.category &&
                section.category !== sheet.sections?.[sectionIndex - 1]?.category && (
                  <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-slate2">
                    {section.category}
                  </span>
                )}
            </h3>
            <div className="mt-3 grid min-w-0 gap-3 md:grid-cols-2">
              {section.entry_ids
                .map((id) => sheet.entries.find((entry) => entry.id === id))
                .filter((entry) => entry !== undefined)
                .map((entry) => (
                  <article
                    key={entry.id}
                    className="min-w-0 max-w-full rounded-2xl border border-hairline bg-canvas-pure p-4"
                  >
                    {entry.title !== section.title && (
                      <h4 className="font-extrabold text-ink">{entry.title}</h4>
                    )}
                    <ReferenceContent entry={entry} />
                    {sheet.diagrams
                      ?.filter((diagram) => diagram.entry_ids.includes(entry.id))
                      .map((diagram) => (
                        <SheetDiagram key={diagram.id} diagram={diagram} />
                      ))}
                  </article>
                ))}
            </div>
          </section>
        ))}
      </div>
      {!sheet.sections && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {sheet.entries.map((entry, index) => (
            <article
              key={entry.id}
              className="min-w-0 rounded-3xl border border-hairline bg-canvas-pure p-4 shadow-card-soft"
            >
              <h3 className="flex items-center gap-3 text-lg font-extrabold text-ink">
                <span
                  className={`rounded-xl border-b-4 px-3 py-2 text-sm ${formulaTints[index % formulaTints.length]}`}
                >
                  {String(index + 1).padStart(2, '0')}
                </span>
                {entry.title}
              </h3>
              <p
                className={`my-4 max-w-full overflow-x-auto rounded-2xl border-b-4 px-3 py-4 text-center text-xl sm:text-3xl font-extrabold text-ink ${formulaTints[index % formulaTints.length]}`}
                aria-label={`Formula: ${entry.formula}`}
              >
                {entry.formula}
              </p>
              <p className="text-sm font-bold text-slate2">{entry.symbols}</p>
              <p className="mt-4 text-sm font-semibold leading-6 text-ink-soft">{entry.use_when}</p>
              <p className="mt-4 border-t border-hairline pt-4 text-sm font-extrabold text-ink">
                {entry.example}
              </p>
              <p className="mt-3 rounded-xl bg-surface p-3 text-sm leading-5 text-ink-soft">
                <span className="font-extrabold">Watch out: </span>
                {entry.mistake}
              </p>
            </article>
          ))}
        </div>
      )}
      {sheet.diagrams
        ?.filter((diagram) => diagram.entry_ids.length === 0)
        .map((diagram) => (
          <SheetDiagram key={diagram.id} diagram={diagram} />
        ))}
      {sheet.challenge && (
        <div className="rounded-3xl border border-brand-sky/30 bg-wash-sky p-6">
          <h3 className="font-extrabold text-ink">Try it without the sheet</h3>
          <p className="mt-2 text-sm font-semibold leading-6 text-ink-soft">{sheet.challenge}</p>
          <button
            type="button"
            className="btn-pill-ghost mt-3"
            aria-expanded={answer}
            onClick={() => setAnswer((value) => !value)}
          >
            {answer ? 'Hide answer' : 'Check your answer'}
          </button>
          {answer && <p className="mt-3 font-extrabold text-ink">{sheet.challenge_answer}</p>}
        </div>
      )}
    </div>
  );
}

function SheetReader({ sheet }: { sheet: OfficialCheatsheet }) {
  const [paper, setPaper] = useState<'A4' | 'A3'>(sheet.html_papers ? 'A3' : 'A4');
  const [colour, setColour] = useState<'colour' | 'mono'>('colour');
  const [view, setView] = useState<'screen' | 'print' | 'fixed'>('screen');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pdf, setPdf] = useState<Blob | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const [expanded, setExpanded] = useState(false);
  const artifact = sheet.artifacts.find((item) => item.paper === paper && item.colour === colour);
  useEffect(() => {
    let cancelled = false;
    setPdf(null);
    setError('');
    if (view === 'print' && artifact) {
      setBusy(true);
      void verifiedCheatsheetPdf(sheet, artifact)
        .then((blob) => {
          if (!cancelled) setPdf(blob);
        })
        .catch((reason: unknown) => {
          if (!cancelled)
            setError(reason instanceof Error ? reason.message : 'PDF could not load. Try again.');
        })
        .finally(() => {
          if (!cancelled) setBusy(false);
        });
    } else setBusy(false);
    return () => {
      cancelled = true;
    };
  }, [sheet, artifact, view]);
  async function download() {
    if (!artifact || busy) return;
    setBusy(true);
    setError('');
    try {
      const blob = await verifiedCheatsheetPdf(sheet, artifact);
      saveCheatsheetPdf(blob, artifact.filename);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Download failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }
  const paperFullscreen = expanded && view === 'fixed';
  const content = (
    <div
      className={`min-w-0 max-w-full ${expanded ? (paperFullscreen ? 'space-y-2' : 'space-y-4') : 'space-y-3 rounded-3xl border border-hairline bg-canvas-pure p-4 shadow-card-soft'}`}
    >
      <header className="relative flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2 text-xs font-bold text-slate2">
            <span className="rounded-full bg-wash-sky px-3 py-1.5 text-ink">
              Year {sheet.year} · {subjectLabel(sheet.subject)}
            </span>
            <span>
              {isLocalStudyExperience() && sheet.key.startsWith('example-')
                ? 'Example reference · pre-authored'
                : sheet.development_review
                  ? 'Development review'
                  : 'Official Airbotix'}
            </span>
          </p>
          <h2
            id="cheatsheet-reader-title"
            className={`mt-2 font-extrabold leading-snug text-ink ${paperFullscreen ? 'text-base sm:text-lg' : 'text-xl sm:text-3xl'}`}
          >
            {sheet.title}
          </h2>
          {!paperFullscreen && sheet.term_scope?.status === 'unconfirmed' && (
            <p className="mt-2 text-xs font-semibold text-slate2">
              General revision draft · School term units are not yet confirmed.
            </p>
          )}
        </div>
        {expanded ? (
          <button
            type="button"
            className="btn-pill-ghost"
            onClick={() => dialog.current?.close()}
            aria-label="Close fullscreen"
          >
            <X size={20} aria-hidden="true" />
            Close
          </button>
        ) : (
          <button
            type="button"
            className="btn-pill-ghost min-h-11 border border-hairline bg-canvas-pure !px-3"
            onClick={() => {
              setExpanded(true);
              dialog.current?.showModal();
            }}
          >
            <Maximize2 size={18} aria-hidden="true" />
            <span className="sr-only sm:not-sr-only">Fullscreen</span>
          </button>
        )}
      </header>
      <div className="relative flex flex-wrap items-center gap-2 border-b border-hairline pb-3">
        {(sheet.fixed_html || sheet.artifacts.length > 0) && (
          <div
            className="flex max-w-full gap-1 overflow-x-auto rounded-2xl bg-surface p-1"
            aria-label="Reading mode"
          >
            {(
              [
                'screen',
                ...(sheet.fixed_html ? ['fixed'] : []),
                ...(sheet.artifacts.length ? ['print'] : []),
              ] as Array<'screen' | 'fixed' | 'print'>
            ).map((mode) => (
              <button
                key={mode}
                type="button"
                aria-pressed={view === mode}
                className={`btn-pill-ghost min-h-11 shrink-0 !rounded-xl !px-3 !text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink ${view === mode ? '!bg-canvas-pure !text-ink shadow-card-soft ring-1 ring-hairline' : 'text-slate2'}`}
                onClick={() => setView(mode)}
              >
                {mode === 'screen'
                  ? 'Responsive HTML'
                  : mode === 'fixed'
                    ? `Fixed ${sheet.html_papers ? paper : 'A3'} layout`
                    : 'Print preview'}
              </button>
            ))}
          </div>
        )}
        {!sheet.html_review && view === 'screen' && <ReferenceNavigation sheet={sheet} />}
        {sheet.artifacts.length > 0 || sheet.html_papers ? (
          <>
            <label className="text-sm font-bold text-ink">
              <span className="sr-only">Paper size</span>
              <select
                aria-label="Paper size"
                className="input-k12 min-h-11 !w-auto !rounded-xl !px-3 !py-2"
                value={paper}
                onChange={(event) => setPaper(event.target.value as 'A4' | 'A3')}
              >
                <option>A4</option>
                <option>A3</option>
              </select>
            </label>
            <label className="text-sm font-bold text-ink">
              <span className="sr-only">Print style</span>
              <select
                aria-label="Print style"
                className="input-k12 min-h-11 !w-auto !rounded-xl !px-3 !py-2"
                value={colour}
                onChange={(event) => setColour(event.target.value as 'colour' | 'mono')}
              >
                <option value="colour">Colour</option>
                <option value="mono">Black & white</option>
              </select>
            </label>
          </>
        ) : null}
        {sheet.artifacts.length > 0 && (
          <button
            type="button"
            className="btn-pill-primary sm:ml-auto"
            disabled={busy || !artifact}
            onClick={() => void download()}
          >
            <Download size={18} aria-hidden="true" />
            {busy
              ? 'Preparing PDF…'
              : sheet.development_review
                ? `Download draft ${paper} PDF`
                : `Download ${paper} PDF`}
          </button>
        )}
      </div>
      {sheet.html_papers && view === 'fixed' && (
        <>
          <style>{`@page { size: ${paper} landscape; margin: 0; }
            @media print {
              html, body, #root { width: ${paper === 'A4' ? 297 : 420}mm; height: ${paper === 'A4' ? 210 : 297}mm; margin: 0; overflow: hidden; }
              body * { visibility: hidden !important; }
              iframe.cheatsheet-print-frame { visibility: visible !important; position: fixed; inset: 0; width: ${paper === 'A4' ? 297 : 420}mm; height: ${paper === 'A4' ? 210 : 297}mm; min-height: 0; border: 0; border-radius: 0; }
            }`}</style>
          <button type="button" className="btn-pill-ghost" onClick={() => window.print()}>
            <Printer size={18} aria-hidden="true" /> Print HTML / Save PDF
          </button>
          <p className={`text-xs text-slate2 ${paperFullscreen ? 'hidden' : ''}`}>
            Select {paper} landscape and 100% scale. Draft printout; school scope is awaiting
            review.
          </p>
        </>
      )}
      {sheet.artifacts.length > 0 && view !== 'screen' && (
        <p className="flex items-start gap-2 text-sm font-semibold text-slate2">
          <Printer size={18} className="shrink-0" aria-hidden="true" />
          {artifact?.pages} {artifact?.pages === 1 ? 'page' : 'pages'} ·{' '}
          {artifact?.orientation ?? ''} · Airbotix corner logo included. Print at actual size (100%)
          on {paper} paper.
        </p>
      )}
      {error && (
        <p
          role="alert"
          className="rounded-2xl border border-brand-coral/40 bg-wash-coral p-4 text-sm font-semibold text-ink"
        >
          {error}
        </p>
      )}
      {sheet.html_review && view !== 'print' ? (
        <CheatsheetHtmlReview
          sheet={sheet}
          fixed={view === 'fixed'}
          paper={sheet.html_papers ? paper : 'A3'}
          colour={colour}
          fullscreen={paperFullscreen}
        />
      ) : view === 'screen' ? (
        <FormulaCards sheet={sheet} />
      ) : pdf ? (
        <CheatsheetPdfPreview blob={pdf} expectedPages={artifact?.pages} />
      ) : (
        !error && (
          <p role="status" className="p-8 text-center font-bold text-slate2">
            Loading the exact PDF you will download…
          </p>
        )
      )}
      {!paperFullscreen && (
        <details className="border-t border-hairline pt-3 text-sm text-slate2">
          <summary className="cursor-pointer font-semibold">About this reference</summary>
          <p className="mt-2 max-w-3xl leading-6">
            {sheet.html_review
              ? 'HTML reference with formulas, explanations, examples and labelled diagrams.'
              : sheet.intro}
          </p>
        </details>
      )}
      <footer
        className={`${paperFullscreen ? 'hidden' : 'flex'} flex-wrap items-center justify-between gap-4 border-t border-hairline pt-5`}
      >
        <img src="/logo-black-horizontal-approved-20261021.png" alt="Airbotix" className="h-7 w-auto" />
        <p className="max-w-xl text-xs font-semibold leading-5 text-slate2">
          {sheet.html_review
            ? `HTML reference · Content v${sheet.version}`
            : `Version ${sheet.version}`}{' '}
          · {sheet.scope}
        </p>
      </footer>
    </div>
  );
  return (
    <>
      {!expanded && content}
      <dialog
        ref={dialog}
        aria-labelledby="cheatsheet-reader-title"
        onClose={() => setExpanded(false)}
        className="fixed inset-0 m-0 h-[100dvh] max-h-none w-screen max-w-none overflow-y-auto bg-canvas p-5 backdrop:bg-ink/60 sm:p-10"
      >
        {expanded && <div className="mx-auto max-w-[1700px]">{content}</div>}
      </dialog>
    </>
  );
}

export function StudyCheatsheetsPage() {
  const [params, setParams] = useSearchParams();
  const filters = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    function dismiss(event: PointerEvent) {
      const panel = filters.current;
      if (panel?.open && event.target instanceof Node && !panel.contains(event.target)) {
        panel.open = false;
      }
    }
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, []);
  const me = useMe();
  const review = useQuery({
    queryKey: ['study-cheatsheet-review', getKidSub()],
    queryFn: listReviewCheatsheets,
    enabled: reviewEnabled && me.data?.kind === 'kid',
    retry: false,
  });
  const home = useQuery({ queryKey: STUDY_HOME_QUERY_KEY, queryFn: getStudyHome, retry: false });
  const catalogue = useQuery({
    queryKey: ['official-cheatsheets'],
    queryFn: listOfficialCheatsheets,
    retry: false,
    staleTime: 0,
  });
  const profileYear = home.data?.academic_year?.year_level;
  const requestedYear = params.get('year');
  const year = requestedYear ?? 'all';
  const validYear = ['all', '6', '7', '8', '9', '10', '11', '12'].includes(year) ? year : 'all';
  const requestedTerm = params.get('term') ?? 'all';
  const term = ['all', '1', '2', '3', '4'].includes(requestedTerm) ? requestedTerm : 'all';
  const requestedSubject = params.get('subject') ?? 'all';
  const subject = ['all', 'mathematics', 'chemistry', 'physics'].includes(requestedSubject)
    ? requestedSubject
    : 'all';
  const availableSheets = [
    ...(catalogue.data?.sheets ?? []),
    ...(review.data?.sheets ?? []).filter(
      (draft) =>
        !(catalogue.data?.sheets ?? []).some(
          (published) => published.key === draft.key && published.version === draft.version,
        ),
    ),
  ];
  const sheets = availableSheets.filter(
    (sheet) =>
      (subject === 'all' || sheet.subject === subject) &&
      (validYear === 'all' || sheet.year === Number(validYear)) &&
      (term === 'all' || sheet.term === Number(term)),
  );
  const requestedSheet = params.get('sheet');
  const selected = requestedSheet
    ? sheets.find(
        (sheet) =>
          sheet.key === requestedSheet &&
          (!params.get('version') || sheet.version === params.get('version')),
      )
    : sheets[0];
  function selectYear(value: string) {
    const next = new URLSearchParams(params);
    next.set('year', value);
    next.delete('sheet');
    next.delete('version');
    setParams(next);
  }
  function selectTerm(value: string) {
    const next = new URLSearchParams(params);
    next.set('term', value);
    next.delete('sheet');
    next.delete('version');
    setParams(next);
  }
  function selectSubject(value: string) {
    const next = new URLSearchParams(params);
    next.set('subject', value);
    next.delete('sheet');
    next.delete('version');
    setParams(next);
  }
  return (
    <StudyShell compactContent>
      <header className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="flex items-center gap-3 text-2xl font-extrabold tracking-hero text-ink">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-wash-sky">
              <FileText size={22} className="text-ink" aria-hidden="true" />
            </span>
            Cheatsheets
          </h1>
          {isLocalStudyExperience() && (
            <Link className="btn-pill-secondary" to={sampleAnalysisPath}>
              Back to Alex’s worksheet
            </Link>
          )}
          <p className="hidden rounded-full bg-surface px-3 py-2 text-xs font-semibold text-slate2 sm:block">
            {me.data?.kind === 'kid'
              ? `Study learner: ${me.data.nickname}`
              : 'Loading your Study account…'}
            {profileYear ? ` · Year ${profileYear}` : ''}
          </p>
        </div>

        {reviewEnabled && (
          <p className="mt-2 text-xs text-slate2">Local preview · School scope unconfirmed.</p>
        )}
        {home.isError && (
          <p role="alert">
            Your Study profile could not load. The year selection has not been personalised.
          </p>
        )}
        {review.isError && (
          <p role="alert">
            The local review library could not load.{' '}
            <button type="button" className="btn-pill-ghost" onClick={() => void review.refetch()}>
              Retry review library
            </button>
          </p>
        )}

        <div
          className="relative mt-3 flex min-w-0 items-end gap-2 rounded-2xl border border-hairline bg-surface p-2 sm:px-3"
          aria-label="Cheatsheet library controls"
        >
          {sheets.length === 1 && selected ? (
            <p className="flex min-h-12 min-w-0 flex-1 items-center text-sm font-semibold text-slate2">
              1 matching cheatsheet
            </p>
          ) : (
            <label className="min-w-0 flex-1 px-1 text-xs font-bold text-slate2">
              <span className="sr-only">
                Reference library · {sheets.length} {sheets.length === 1 ? 'sheet' : 'sheets'}
              </span>
              <select
                aria-label="Choose a cheatsheet"
                className="input-k12 min-h-12 min-w-0 !rounded-xl !px-3 !py-2 font-semibold disabled:text-slate2"
                disabled={sheets.length === 0}
                value={selected ? `${selected.key}:${selected.version}` : ''}
                onChange={(event) => {
                  const chosen = sheets.find(
                    (item) => `${item.key}:${item.version}` === event.target.value,
                  );
                  if (!chosen) return;
                  const next = new URLSearchParams(params);
                  next.set('sheet', chosen.key);
                  next.set('version', chosen.version);
                  setParams(next);
                }}
              >
                {sheets.length === 0 && (
                  <option value="">
                    {catalogue.isPending ? 'Loading sheets…' : 'No matching sheets'}
                  </option>
                )}
                {sheets.length > 0 && !selected && (
                  <option value="" disabled>
                    Choose a reference
                  </option>
                )}
                {Array.from(new Set(sheets.map((item) => `${item.subject}:${item.year}`))).map(
                  (group) => {
                    const [groupSubject, groupYear] = group.split(':');
                    return (
                      <optgroup
                        key={group}
                        label={`${subjectLabel(groupSubject)} · Year ${groupYear}`}
                      >
                        {sheets
                          .filter((item) => `${item.subject}:${item.year}` === group)
                          .map((item) => (
                            <option
                              key={`${item.key}:${item.version}`}
                              value={`${item.key}:${item.version}`}
                            >
                              {item.title}
                            </option>
                          ))}
                      </optgroup>
                    );
                  },
                )}
              </select>
            </label>
          )}
          <details
            ref={filters}
            className="group shrink-0"
            onKeyDown={(event) => {
              if (event.key === 'Escape' && filters.current) {
                filters.current.open = false;
                filters.current.querySelector('summary')?.focus();
              }
            }}
          >
            <summary className="btn-pill-ghost min-h-12 cursor-pointer list-none border border-hairline bg-canvas-pure !px-3 group-open:!bg-wash-sky focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink [&::-webkit-details-marker]:hidden">
              <SlidersHorizontal size={17} aria-hidden="true" />
              <span>Filters</span>
              {[validYear, term, subject].filter((value) => value !== 'all').length > 0 && (
                <span className="rounded-full bg-wash-sky px-2 text-xs text-ink">
                  {[validYear, term, subject].filter((value) => value !== 'all').length}
                </span>
              )}
              <ChevronDown size={16} className="group-open:rotate-180" aria-hidden="true" />
              <span className="sr-only">
                Filter library · {validYear === 'all' ? 'All years' : `Year ${validYear}`} ·{' '}
                {term === 'all' ? 'All terms' : `Term ${term}`} ·{' '}
                {subject === 'all' ? 'All subjects' : subjectLabel(subject)}
              </span>
            </summary>
            <div className="absolute inset-x-0 top-full z-20 mt-2 rounded-2xl border border-hairline bg-canvas-pure p-4 shadow-floating-chrome sm:left-auto sm:w-[32rem]">
              <p className="mb-3 text-sm font-extrabold text-ink">Filter your library</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <label className="min-w-0 text-xs font-bold text-slate2">
                  Year
                  <select
                    aria-label="Cheatsheet year"
                    value={validYear}
                    onChange={(event) => selectYear(event.target.value)}
                    className="input-k12 mt-1 min-h-12 min-w-0 !rounded-xl !px-3 !py-2 font-semibold"
                  >
                    <option value="all">All years</option>
                    {[6, 7, 8, 9, 10, 11, 12].map((value) => (
                      <option key={value} value={value}>
                        Year {value}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="min-w-0 text-xs font-bold text-slate2">
                  Term
                  <select
                    aria-label="Cheatsheet term"
                    value={term}
                    onChange={(event) => selectTerm(event.target.value)}
                    className="input-k12 mt-1 min-h-12 min-w-0 !rounded-xl !px-3 !py-2 font-semibold"
                  >
                    <option value="all">All terms</option>
                    {[1, 2, 3, 4].map((value) => (
                      <option key={value} value={value}>
                        Term {value}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="min-w-0 text-xs font-bold text-slate2">
                  Subject
                  <select
                    aria-label="Cheatsheet subject"
                    value={subject}
                    onChange={(event) => selectSubject(event.target.value)}
                    className="input-k12 mt-1 min-h-12 min-w-0 !rounded-xl !px-3 !py-2 font-semibold"
                  >
                    <option value="all">All subjects</option>
                    <option value="mathematics">Mathematics</option>
                    <option value="chemistry">Chemistry</option>
                    <option value="physics">Physics</option>
                  </select>
                </label>
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  className="btn-pill-primary min-h-12 !px-4 !py-2"
                  onClick={() => {
                    if (filters.current) {
                      filters.current.open = false;
                      filters.current.querySelector('summary')?.focus();
                    }
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          </details>
        </div>
      </header>
      {reviewEnabled && review.isPending && (
        <p role="status" className="p-8 font-bold text-slate2">
          Loading saved Study drafts…
        </p>
      )}
      {catalogue.isPending && !review.data && (
        <p role="status" className="p-8 text-center font-bold text-slate2">
          Loading official cheatsheets…
        </p>
      )}
      {catalogue.isError && !review.data && (
        <div role="alert" className="rounded-3xl border border-brand-coral/40 bg-wash-coral p-6">
          <p className="font-bold text-ink">We couldn’t load your cheatsheets.</p>
          <p className="mt-2 text-sm text-ink-soft">
            Check your connection and account access, then try again.
          </p>
          <button
            type="button"
            className="btn-pill-ghost mt-3"
            onClick={() => void catalogue.refetch()}
          >
            Try again
          </button>
        </div>
      )}
      {catalogue.isSuccess && (!reviewEnabled || !review.isPending) && sheets.length === 0 && (
        <section className="rounded-3xl border border-hairline bg-canvas-pure p-8 text-center">
          <FileText size={32} className="mx-auto text-slate2" aria-hidden="true" />
          <h2 className="mt-3 text-xl font-extrabold text-ink">
            No published cheatsheets {validYear === 'all' ? 'yet' : `for Year ${validYear}`}
            {term !== 'all' ? ` · Term ${term}` : ''}
          </h2>
          <p className="mt-2 text-sm font-semibold text-slate2">
            There is no cheatsheet for this choice yet. Try another subject, Term or Year.
          </p>
          {validYear !== 'all' && (
            <button type="button" className="btn-pill-ghost mt-4" onClick={() => selectYear('all')}>
              Browse all years
            </button>
          )}
        </section>
      )}
      {requestedSheet && sheets.length > 0 && !selected && (
        <p
          role="alert"
          className="mb-6 rounded-3xl border border-hairline bg-canvas-pure p-6 font-bold text-ink"
        >
          This cheatsheet version is unavailable. Choose a published reference below.
        </p>
      )}
      {selected && <SheetReader key={`${selected.key}:${selected.version}`} sheet={selected} />}
    </StudyShell>
  );
}
